import { HttpClient } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { Injectable } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {

  latitude: number = null;
  longitude: number = null;

  address: string = null;

  permission: boolean = false;

  permission$: Observable<boolean>;
  private permissionSubject: BehaviorSubject<boolean>;

  get latLng(): {latitude: number, longitude: number} {
    return {
      latitude: this.latitude,
      longitude: this.longitude
    };
  }

  constructor(
    private platform: Platform,
    private http: HttpClient
  ) {
    this.permissionSubject = new BehaviorSubject<boolean>(this.permission);
    this.permission$ = this.permissionSubject.asObservable();
  }

  private options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 3600
  };

  async getCurrentCoordinates() {
    try {
      const permissionStatus = await Geolocation.checkPermissions();

      if (permissionStatus.location === 'denied') {
        this.permission = false;
        this.permissionSubject.next(false);
        return { latitude: null, longitude: null };
      }

      if (permissionStatus.location !== 'granted') {
        const requestPermission = await Geolocation.requestPermissions();
        if (requestPermission.location !== 'granted') {
          this.permission = false;
          this.permissionSubject.next(false);
          return { latitude: null, longitude: null };
        }
      }

      const position = await Geolocation.getCurrentPosition(this.options);

      this.latitude = position.coords.latitude;
      this.longitude = position.coords.longitude;

      await this.updateAddress(this.latitude, this.longitude);

      this.permission = true;
      this.permissionSubject.next(true);

      return position.coords;
    } catch (error) {
      console.log('There was an error fetching your co-ordinates');
      console.log({ error });

      this.permission = false;
      this.permissionSubject.next(false);

      return { latitude: null, longitude: null };
    }
  }

  hasPermission() {
    return this.permission;
  }

  async getAddress() {
    if (this.address === null && this.hasPermission()) {
      if (this.latitude === null && this.longitude === null) {
        await this.getCurrentCoordinates();
      }

      return await this.updateAddress(this.latitude, this.longitude);
    }

    return this.address;
  }

  // get address using coordinates
  async updateAddress(latitude: number, longitude: number) {
    // Since Capacitor Geolocation doesn't provide native geocoding,
    // use the HTTP reverse geocode API fallback regardless of platform

    try {
      const response: any = await this.http
        .get(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`)
        .toPromise();

      this.address = response.display_name;

      return this.address;
    } catch (error) {
      console.error('Error getting address:', error);
      return null;
    }
  }

  // Optionally keep this if you want a custom formatted address string
  pretifyAddress(address: any) {
    let string = '';

    if (address.thoroughfare) string += address.thoroughfare + ', ';
    if (address.subAdministrativeArea) string += address.subAdministrativeArea + ', ';
    if (address.administrativeArea) string += address.administrativeArea + ', ';
    if (address.postalCode) string += address.postalCode;

    return string;
  }

  async getPostcodesFromInput(input: string) {
    const response = await this.http.get('https://api.postcodes.io/postcodes/' + input + '/autocomplete').toPromise();

    return response['result'];
  }
}
